/**
 * AddEditCategoryV2 — full V2 port of the Category edit form, using
 * the redesign/edit primitives + the new SortableChildList. Same
 * state shape, fetch graph, and submit handler as the v1 form so
 * the API contract is byte-identical.
 *
 * Sections:
 *   01 Basic info          — title, description, explore term
 *   02 Sharing & Media     — category image, deep-link image,
 *                            universal link
 *   03 Subcategories       — sortable list (edit-only)
 *   04 Series              — sortable list (edit-only)
 *   05 Sessions / Quick Taps — sortable list with per-row
 *                              featured + dark-mode toggles
 *                              (which list shows depends on the
 *                              category_is_quick_tap flag)
 *   06 Danger zone (edit-only)
 *
 * Right rail:
 *   • PublishingCardCat — type + Select Interest pills + subitem counts
 *   • SettingsCardCat   — 4 toggles
 *
 * Exposes a `submit()` method via ref so the page chrome's Save
 * button can drive the form (same imperative pattern v1 uses).
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';

import LoadingBackdrop from '../../Common/LoadingBackdrop';
import {
  addEditCategory,
  deleteCategory,
} from '../../../actions/categoryActions';
import { apiV2 } from '../../../actions/index';
import {
  uploadImage,
  uploadCategoryImage,
  clearMedia,
} from '../../../actions/mediaActions';
import {
  INITIAL_LIST_STATE,
  createSortHandlers,
} from '../../Common/sortableListUtils';
import { preventEnterFormSubmission } from '../../../utils/formUtils';

import { Button } from '../../redesign';
import {
  FormSection,
  FieldBlock,
  FieldRow,
  TextInput,
  Textarea,
  LinkField,
  UploadTile,
  SortableChildList,
} from '../../redesign/edit';
import PublishingCardCat from './PublishingCardCat';
import SettingsCardCat from './SettingsCardCat';
import PreviewCardCat from './PreviewCardCat';

const AddEditCategoryV2 = forwardRef(function AddEditCategoryV2(
  { isEdit, selectedId, onLoaded },
  ref,
) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const backPath = searchParams.get('from') === 'audiobooks' ? '/audiobooks' : '/category';

  /* ─── State (1:1 with v1) ──────────────────────────────────── */
  const [state, setState] = useState({
    category_title: '',
    category_description: '',
    category_explore_term: '',
    category_is_active: true,
    category_is_dark_mode: false,
    category_is_quick_tap: false,
    category_is_select_interest: true,
    category_image_preview: null,
    category_image: '',
    category_deep_link_image: '',
    deep_link_image_preview: '',
    isSetToEdit: false,
  });

  const [seriesState, setSeriesState] = useState(INITIAL_LIST_STATE);
  const [subcategoriesState, setSubcategoriesState] = useState(INITIAL_LIST_STATE);
  const [sessionsState, setSessionsState] = useState(INITIAL_LIST_STATE);
  const [quickTapsState, setQuickTapsState] = useState(INITIAL_LIST_STATE);
  const [sessionData, setSessionData] = useState({});

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Latest-value ref for the page chrome's onLoaded callback.
  // Lets the load effect stay keyed on `selectedId` alone.
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  const handleSessionDataChange = useCallback((id, data) => {
    setSessionData((prev) => ({
      ...prev,
      [id]: {
        is_featured: data.is_featured ?? 0,
        is_dark_mode: data.is_dark_mode ?? 0,
      },
    }));
  }, []);

  /* ─── Fetch on edit (1:1 with v1) ─────────────────────────── */
  useEffect(() => {
    if (!isEdit) return;
    setIsLoading(true);
    apiV2.get(`/categories/${selectedId}`)
      .then((response) => {
        const category = response.data;
        setState((prev) => ({
          ...prev,
          ...category,
          isSetToEdit: true,
        }));

        if (category.subcategories) {
          setSubcategoriesState({
            items: category.subcategories.map((o) => ({
              id: o.subcategory_id + 'SUBCATEGORY',
              label: o.subcategory_title,
              value: o.subcategory_id + '',
            })),
            isLoaded: true,
            sortOrder: category.subcategories.map((o) => o.subcategory_id + 'SUBCATEGORY'),
            isLoading: false,
          });
        }
        if (category.series) {
          setSeriesState({
            items: category.series.map((o) => ({
              id: o.series_id + 'SERIES',
              label: o.series_title,
              value: o.series_id + '',
            })),
            isLoaded: true,
            sortOrder: category.series.map((o) => o.series_id + 'SERIES'),
            isLoading: false,
          });
        }
        if (category.sessions) {
          const items = category.sessions.map((o) => ({
            id: o.session_id + 'SESSION',
            label: o.session_name,
            value: o.session_id + '',
          }));
          const initialSessionData = category.sessions.reduce((acc, s) => {
            acc[s.session_id + ''] = {
              is_featured: s.session_is_featured || 0,
              is_dark_mode: s.session_is_dark_mode || 0,
            };
            return acc;
          }, {});
          setSessionData((prev) => ({ ...prev, ...initialSessionData }));
          setSessionsState({
            items,
            isLoaded: true,
            sortOrder: items.map((i) => i.id),
            isLoading: false,
          });
        }
        if (category.quick_taps && category.category_is_quick_tap) {
          const items = category.quick_taps.map((o) => ({
            id: o.id + 'QUICKTAP',
            label: o.quick_tap_title,
            value: o.id + '',
          }));
          const initialQtData = category.quick_taps.reduce((acc, q) => {
            acc[q.id + ''] = {
              is_featured: q.session_is_featured || q.is_featured || 0,
              is_dark_mode: q.session_is_dark_mode || q.is_dark_mode || 0,
            };
            return acc;
          }, {});
          setSessionData((prev) => ({ ...prev, ...initialQtData }));
          setQuickTapsState({
            items,
            isLoaded: true,
            sortOrder: items.map((i) => i.id),
            isLoading: false,
          });
        }

        // Hand title + updated_at up to the page chrome so it
        // doesn't fire its own parallel GET /categories/{id}.
        if (onLoadedRef.current) {
          onLoadedRef.current({
            category_title: category.category_title,
            category_updated_at:
              category.category_updated_at ?? category.updated_at ?? null,
          });
        }

        setIsLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching category:', error);
        setIsLoading(false);
      });
  }, [isEdit, selectedId]);

  /* ─── Field handlers ──────────────────────────────────────── */
  const setField = (name, value) => setState((prev) => ({ ...prev, [name]: value }));
  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setField(name, value);
  };

  /* ─── Media uploads (1:1 with v1) ─────────────────────────── */
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (state.category_image_preview) window.URL.revokeObjectURL(state.category_image_preview);
    setState((prev) => ({ ...prev, category_image_preview: window.URL.createObjectURL(file) }));
    dispatch(uploadCategoryImage(selectedId, file)).then((response) => {
      setState((prev) => ({ ...prev, category_image: response.data.image_url }));
      dispatch(clearMedia());
    });
  };

  const handleDeepLinkImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (state.deep_link_image_preview) window.URL.revokeObjectURL(state.deep_link_image_preview);
    setState((prev) => ({ ...prev, deep_link_image_preview: window.URL.createObjectURL(file) }));
    dispatch(uploadImage(file)).then((response) => {
      setState((prev) => ({ ...prev, category_deep_link_image: response.data.image_url }));
      dispatch(clearMedia());
    });
  };

  /* Preview-URL cleanup on unmount. Latest-value ref so the unmount
   * callback reads the most recent blob URLs without forcing the
   * effect to re-run on every preview change. */
  const previewUrlsRef = useRef({
    category_image_preview: null,
    deep_link_image_preview: '',
  });
  previewUrlsRef.current.category_image_preview = state.category_image_preview;
  previewUrlsRef.current.deep_link_image_preview = state.deep_link_image_preview;
  useEffect(() => {
    return () => {
      const refs = previewUrlsRef.current;
      if (refs.category_image_preview) window.URL.revokeObjectURL(refs.category_image_preview);
      if (refs.deep_link_image_preview) window.URL.revokeObjectURL(refs.deep_link_image_preview);
    };
  }, []);

  /* ─── Sort/remove factory (mirrors v1) ───────────────────── */
  const seriesHandlers = useMemo(() => createSortHandlers(setSeriesState), []);
  const subcategoriesHandlers = useMemo(() => createSortHandlers(setSubcategoriesState), []);
  const sessionsHandlers = useMemo(() => createSortHandlers(setSessionsState), []);
  const quickTapsHandlers = useMemo(() => createSortHandlers(setQuickTapsState), []);

  /* ─── Submit (1:1 with v1) ───────────────────────────────── */
  const formRef = useRef(null);

  const handleSubmit = (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!state.category_title) {
      Swal.fire({
        icon: 'error',
        text: 'Category name is required',
        confirmButtonText: 'Ok',
      });
      return;
    }
    if (state.category_is_quick_tap && state.category_is_select_interest) {
      Swal.fire({
        icon: 'warning',
        text: 'Quick Tap Categories cannot be in the Select Interest section',
        confirmButtonText: 'Ok',
      });
      return;
    }
    setIsSaving(true);

    /* Parse child_objects_json — mirrors v1 exactly. */
    const child_objects_json = [];
    let sortNumber = 1;

    // Series
    seriesState.sortOrder.forEach((id) => {
      const item = seriesState.items.find((i) => i.id === id);
      if (!item || !item.value) return;
      child_objects_json.push({
        object_id: item.value,
        is_sub: 0,
        is_series: 1,
        is_quick_tap: 0,
        sort_number: sortNumber++,
      });
    });

    // Subcategories
    subcategoriesState.sortOrder.forEach((id) => {
      const item = subcategoriesState.items.find((i) => i.id === id);
      if (!item || !item.value) return;
      child_objects_json.push({
        object_id: item.value,
        is_sub: 1,
        is_series: 0,
        is_quick_tap: 0,
        sort_number: sortNumber++,
      });
    });

    if (state.category_is_quick_tap) {
      quickTapsState.sortOrder.forEach((id) => {
        const item = quickTapsState.items.find((i) => i.id === id);
        if (!item || !item.value) return;
        child_objects_json.push({
          object_id: item.value,
          is_sub: 0,
          is_series: 0,
          is_quick_tap: 1,
          sort_number: sortNumber++,
          session_data: {
            session_is_featured: sessionData[item.value]?.is_featured || 0,
            session_is_dark_mode: sessionData[item.value]?.is_dark_mode || 0,
          },
        });
      });
    } else {
      sessionsState.sortOrder.forEach((id) => {
        const item = sessionsState.items.find((i) => i.id === id);
        if (!item || !item.value) return;
        child_objects_json.push({
          object_id: item.value,
          is_sub: 0,
          is_series: 0,
          is_quick_tap: 0,
          sort_number: sortNumber++,
          session_data: {
            session_is_featured: sessionData[item.value]?.is_featured || 0,
            session_is_dark_mode: sessionData[item.value]?.is_dark_mode || 0,
          },
        });
      });
    }

    // Build the JSON payload from an explicit allow-list rather than
    // spreading `state` and trying to strip the unwanted bits. The
    // fetch effect spreads the full API response into `state`
    // (including child arrays + any other server-side fields the
    // endpoint happens to return), so the rest-destructure pattern
    // can't reliably enumerate everything that shouldn't be on the
    // wire. The action thunk (`addEditCategory`) already destructures
    // a fixed allow-list from this object, so this just brings the
    // CMS side in line with the same contract — anything the API
    // doesn't accept never makes it past this boundary.
    const categoryData = {
      category_id: state.category_id,
      category_title: state.category_title,
      category_description: state.category_description,
      category_explore_term: state.category_explore_term,
      category_image: state.category_image,
      category_deep_link_image: state.category_deep_link_image,
      category_deep_link_android: state.category_deep_link_android,
      category_deep_link_ios: state.category_deep_link_ios,
      category_is_active: state.category_is_active ? 1 : 0,
      category_is_dark_mode: state.category_is_dark_mode ? 1 : 0,
      category_is_quick_tap: state.category_is_quick_tap ? 1 : 0,
      category_is_select_interest: state.category_is_select_interest ? 1 : 0,
    };

    dispatch(addEditCategory({
      category_json: JSON.stringify(categoryData),
      child_objects_json: JSON.stringify(child_objects_json),
    }))
      .then(() => {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: `Category ${isEdit ? 'updated' : 'created'} successfully`,
          confirmButtonText: 'Ok',
        }).then(() => navigate(backPath));
      })
      .catch((error) => {
        console.error('Error saving category:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'Failed to save category',
          confirmButtonText: 'Ok',
        });
      })
      .finally(() => setIsSaving(false));
  };

  // Expose the imperative submit() the v1 page chrome uses.
  useImperativeHandle(ref, () => ({
    submit: () => handleSubmit({ preventDefault: () => {} }),
  }));

  /* ─── Delete (1:1 with v1) ──────────────────────────────── */
  const handleDelete = () => {
    Swal.fire({
      icon: 'warning',
      text: 'Are you sure you want to delete this category?',
      showConfirmButton: true,
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonText: 'Delete Category',
    }).then((result) => {
      if (result.value) {
        dispatch(deleteCategory(state.category_id, () => navigate(backPath)));
      }
    });
  };

  /* ─── Render ─────────────────────────────────────────────── */
  if (isEdit && (isLoading || !state.isSetToEdit)) {
    return <LoadingBackdrop open />;
  }

  const counts = {
    subcategories: subcategoriesState.sortOrder.length,
    series: seriesState.sortOrder.length,
    sessions: sessionsState.sortOrder.length,
    quickTaps: quickTapsState.sortOrder.length,
  };

  return (
    <>
      <LoadingBackdrop open={isSaving} />
      <div
        className="edit-canvas grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] items-start"
        style={{ gap: 24 }}
      >
        <form
          id="category-form"
          ref={formRef}
          onSubmit={handleSubmit}
          onKeyDown={preventEnterFormSubmission}
          autoComplete="off"
          style={{ minWidth: 0 }}
        >
          {/* 01 Basic info */}
          <FormSection title="Basic info" num="01">
            <FieldBlock label="Category name" required htmlFor="f-cat-title">
              <TextInput
                id="f-cat-title"
                name="category_title"
                value={state.category_title}
                onChange={handleFieldChange}
                required
              />
            </FieldBlock>
            <FieldBlock label="Description" htmlFor="f-cat-desc">
              <Textarea
                id="f-cat-desc"
                name="category_description"
                value={state.category_description || ''}
                onChange={handleFieldChange}
                rows={2}
                placeholder="Optional — shown on the in-app category page."
              />
            </FieldBlock>
            <FieldBlock
              label="Explore-more search term"
              hint="Powers the in-app Explore More search results"
              htmlFor="f-cat-explore"
              noMargin
            >
              <TextInput
                id="f-cat-explore"
                name="category_explore_term"
                value={state.category_explore_term || ''}
                onChange={handleFieldChange}
              />
            </FieldBlock>
          </FormSection>

          {/* 02 Sharing & Media */}
          <FormSection title="Sharing & Media" num="02">
            <FieldRow cols={2} minColWidth={240}>
              <UploadTile
                label="Category Image"
                value={state.category_image_preview || state.category_image}
                accept="image/*"
                onChange={handleImageUpload}
                onRemove={() => {
                  if (state.category_image_preview) window.URL.revokeObjectURL(state.category_image_preview);
                  setState((prev) => ({ ...prev, category_image: '', category_image_preview: null }));
                }}
              />
              <UploadTile
                label="Deep Link Image"
                hint="Social share card"
                value={state.deep_link_image_preview || state.category_deep_link_image}
                accept="image/*"
                onChange={handleDeepLinkImageUpload}
                onRemove={() => {
                  if (state.deep_link_image_preview) window.URL.revokeObjectURL(state.deep_link_image_preview);
                  setState((prev) => ({ ...prev, category_deep_link_image: '', deep_link_image_preview: '' }));
                }}
              />
            </FieldRow>
            {isEdit && state.universal_link && (
              <div style={{ marginTop: 16 }}>
                <FieldBlock
                  label="Universal Link"
                  hint="Generated automatically · used for share & deep links"
                  noMargin
                >
                  <LinkField value={state.universal_link} />
                </FieldBlock>
              </div>
            )}
          </FormSection>

          {/* Subitem lists — edit-only, matches v1 visibility */}
          {isEdit && (
            <>
              {/* 03 Subcategories */}
              <FormSection
                title="Subcategories"
                num="03"
                right={
                  <span className="text-text-muted" style={{ fontSize: 11.5 }}>
                    {subcategoriesState.sortOrder.length}
                  </span>
                }
              >
                <SortableChildList
                  items={subcategoriesState.items}
                  sortOrder={subcategoriesState.sortOrder}
                  onRemove={subcategoriesHandlers.onRemove}
                  onSortEnd={subcategoriesHandlers.onSort}
                  emptyText="No subcategories yet."
                />
              </FormSection>

              {/* 04 Series */}
              <FormSection
                title="Series"
                num="04"
                right={
                  <span className="text-text-muted" style={{ fontSize: 11.5 }}>
                    {seriesState.sortOrder.length}
                  </span>
                }
              >
                <SortableChildList
                  items={seriesState.items}
                  sortOrder={seriesState.sortOrder}
                  onRemove={seriesHandlers.onRemove}
                  onSortEnd={seriesHandlers.onSort}
                  emptyText="No series yet."
                />
              </FormSection>

              {/* 05 Sessions OR Quick Taps */}
              {state.category_is_quick_tap ? (
                <FormSection
                  title="Quick Taps"
                  num="05"
                  right={
                    <span className="text-text-muted" style={{ fontSize: 11.5 }}>
                      {quickTapsState.sortOrder.length}
                    </span>
                  }
                >
                  <SortableChildList
                    items={quickTapsState.items}
                    sortOrder={quickTapsState.sortOrder}
                    onRemove={quickTapsHandlers.onRemove}
                    onSortEnd={quickTapsHandlers.onSort}
                    showSwitches
                    onSessionDataChange={handleSessionDataChange}
                    sessionData={sessionData}
                    emptyText="No Quick Taps in this category yet."
                  />
                </FormSection>
              ) : (
                <FormSection
                  title="Sessions"
                  num="05"
                  right={
                    <span className="text-text-muted" style={{ fontSize: 11.5 }}>
                      {sessionsState.sortOrder.length}
                    </span>
                  }
                >
                  <SortableChildList
                    items={sessionsState.items}
                    sortOrder={sessionsState.sortOrder}
                    onRemove={sessionsHandlers.onRemove}
                    onSortEnd={sessionsHandlers.onSort}
                    showSwitches
                    onSessionDataChange={handleSessionDataChange}
                    sessionData={sessionData}
                    emptyText="No sessions in this category yet."
                  />
                </FormSection>
              )}
            </>
          )}

          {/* 06 Danger zone */}
          {isEdit && (
            <FormSection title="Danger zone" num="06">
              <div className="flex items-center" style={{ gap: 12, flexWrap: 'wrap' }}>
                <Button variant="danger" onClick={handleDelete} type="button">
                  Delete category
                </Button>
                <span className="text-text-muted" style={{ fontSize: 12 }}>
                  Permanent — cannot be undone.
                </span>
              </div>
            </FormSection>
          )}

          {/* When the form is empty (new), there's no add-list yet —
              just the Save button works from the page chrome. */}
          {!isEdit && (
            <FormSection title="Subitems" num="03">
              <p className="text-text-muted" style={{ fontSize: 13 }}>
                Save the category first; you'll be able to attach subcategories,
                series, and sessions on the next screen.
              </p>
            </FormSection>
          )}
        </form>

        {/* Right rail */}
        <aside
          className="edit-rail"
          style={{
            alignSelf: 'start',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            minWidth: 0,
          }}
        >
          <PreviewCardCat
            state={state}
            subitemCount={
              state.category_is_quick_tap ? counts.quickTaps : counts.sessions
            }
          />
          {isEdit && (
            <PublishingCardCat
              state={state}
              counts={counts}
              onToggleActive={(next) => setField('category_is_active', !!next)}
            />
          )}
          <SettingsCardCat
            state={state}
            onChange={(name, value) => setField(name, !!value)}
          />
        </aside>
      </div>
    </>
  );
});

export default AddEditCategoryV2;
